import React, { useRef, useEffect } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

export function RichTextEditor({ 
  value = '', 
  onChange, 
  placeholder = 'Start typing...', 
  minHeight = '200px',
  className = '',
  id = ''
}) {
  const editorRef = useRef(null);
  const isInitializedRef = useRef(false);
  
  // Sync value when it changes externally (but not during typing)
  useEffect(() => {
    if (editorRef.current && isInitializedRef.current) {
      const currentData = editorRef.current.getData();
      // Only update if the value is significantly different (avoiding cursor jump)
      if (value !== currentData && value !== undefined) {
        editorRef.current.setData(value || '');
      }
    }
  }, [value]);

  const editorConfig = {
    licenseKey: 'GPL',
    placeholder,
    toolbar: [
      'heading',
      '|',
      'bold',
      'italic',
      '|',
      'bulletedList',
      'numberedList',
      '|',
      'outdent',
      'indent',
      '|',
      'blockQuote',
      'insertTable',
      '|',
      'link',
      '|',
      'undo',
      'redo'
    ],
    heading: {
      options: [
        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
        { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
        { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
        { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' }
      ]
    },
    table: {
      contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
    }
  };

  return (
    <div className={`ckeditor-wrapper ${className}`} data-testid={`rich-text-editor-${id}`}>
      <CKEditor
        editor={ClassicEditor}
        data={value || ''}
        config={editorConfig}
        onReady={(editor) => {
          editorRef.current = editor;
          isInitializedRef.current = true;
          // Ensure initial data is set correctly
          if (value && editor.getData() !== value) {
            editor.setData(value);
          }
        }}
        onChange={(event, editor) => {
          const data = editor.getData();
          if (onChange) {
            onChange(data);
          }
        }}
      />
      <style>{`
        .ckeditor-wrapper {
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          overflow: hidden;
        }
        .ckeditor-wrapper .ck-editor__editable {
          min-height: ${minHeight};
          font-size: 14px;
          line-height: 1.6;
        }
        .ckeditor-wrapper .ck-editor__editable:focus {
          border-color: #1e3a5f !important;
          box-shadow: none !important;
        }
        .ckeditor-wrapper .ck.ck-toolbar {
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
        }
        .ckeditor-wrapper .ck.ck-button {
          cursor: pointer;
        }
        .ckeditor-wrapper .ck.ck-button.ck-on {
          background: #e2e8f0;
        }
        .ckeditor-wrapper .ck-content h1 {
          font-size: 1.75rem;
          font-weight: 700;
          margin: 1rem 0;
          color: #1e3a5f;
        }
        .ckeditor-wrapper .ck-content h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 0.875rem 0;
          color: #1e3a5f;
        }
        .ckeditor-wrapper .ck-content h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0.75rem 0;
          color: #1e3a5f;
        }
        .ckeditor-wrapper .ck-content p {
          margin: 0.5rem 0;
        }
        .ckeditor-wrapper .ck-content ul,
        .ckeditor-wrapper .ck-content ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .ckeditor-wrapper .ck-content ul {
          list-style-type: disc;
        }
        .ckeditor-wrapper .ck-content ol {
          list-style-type: decimal;
        }
        .ckeditor-wrapper .ck-content li {
          margin: 0.25rem 0;
        }
        .ckeditor-wrapper .ck-content blockquote {
          border-left: 4px solid #d4af37;
          padding-left: 1rem;
          margin: 1rem 0;
          font-style: italic;
          color: #64748b;
        }
        .ckeditor-wrapper .ck-content table {
          border-collapse: collapse;
          width: 100%;
          margin: 1rem 0;
        }
        .ckeditor-wrapper .ck-content table td,
        .ckeditor-wrapper .ck-content table th {
          border: 1px solid #e2e8f0;
          padding: 0.5rem;
        }
        .ckeditor-wrapper .ck-content table th {
          background: #f8fafc;
          font-weight: 600;
        }
        .ckeditor-wrapper .ck-content a {
          color: #d4af37;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}

export default RichTextEditor;
